import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import * as proposalSelectors from '@makemydeal/dr-dash-store/selectors/proposalSelectors';

import {
    TEXT_INPUT_MAX_LENGTH,
    MAX_DISCLAIMER_LENGTH,
    DISPLAY_CONFIGURATION_LABELS,
    type FieldConfigurationKey,
    type DollarValueRounding,
    type FontSizeAdjustment,
    type DisplayItemKey,
    type DisplayConfigState,
    type FourSquareFieldKey,
    type FourSquareDisplayKey
} from './constants';

export type FieldConfigurationState = Record<
    FieldConfigurationKey,
    {
        active: boolean;
        bold: boolean;
        displayName: string;
        order: number;
    }
>;

export interface ProposalFormPayload {
    title: string;
    fieldConfiguration: FieldConfigurationState;
    displayConfiguration: DisplayConfigState;
}

export interface UseProposalFormOptions {
    isEditMode?: boolean;
    /**
     * Predicate deciding whether a field-configuration key should be kept.
     * Callers compose label-based filtering with toggle-based hiding here.
     */
    fieldKeyFilter: (key: string) => boolean;
    /**
     * Predicate deciding whether a display-configuration key should be kept.
     */
    displayKeyFilter: (key: string) => boolean;
}

export interface UseProposalFormResult {
    // Form state
    activeTab: string;
    setActiveTab: (tab: string) => void;
    proposalName: string;
    setProposalName: (name: string) => void;
    fieldConfiguration: FieldConfigurationState;
    displayConfiguration: DisplayConfigState;

    // Loading / error flags
    isLoadingProposal: boolean;
    isProposalError: boolean;
    proposalToEdit: ReturnType<typeof proposalSelectors.getCurrentProposal>;
    isCreatingProposal: boolean;
    hasCreationError: boolean;
    creationErrorMessage: string | undefined;
    isCreationSuccess: boolean;
    isUpdatingProposal: boolean;
    hasUpdateError: boolean;
    updateErrorMessage: string | undefined;
    isUpdateSuccess: boolean;

    // Validation
    hasError: boolean;

    // Handlers
    handleDisplayNameChangeForFieldConfigItem: (fieldKey: FieldConfigurationKey, newDisplayName: string) => void;
    handleBoldChangeForFieldConfigItem: (fieldKey: FieldConfigurationKey, checked: boolean) => void;
    handleActiveChangeForFieldConfigItem: (fieldKey: FieldConfigurationKey | FourSquareFieldKey, checked: boolean) => void;
    handleOrderChangeForFieldConfig: (orderedKeys: FieldConfigurationKey[]) => void;
    handleDollarValueRoundingChange: (value: DollarValueRounding) => void;
    handleFontSizeAdjustmentChange: (value: FontSizeAdjustment) => void;
    handleActiveChangeForDisplayConfigItem: (displayKey: DisplayItemKey | FourSquareDisplayKey, checked: boolean) => void;
    handleValueChangeForDisplayConfigItem: (displayKey: DisplayItemKey | FourSquareDisplayKey, value: string | undefined) => void;
    handleDisplayNameChangeForDisplayConfigItem: (displayKey: DisplayItemKey, newDisplayName: string) => void;

    // Form payload (for ref forwarding)
    getFormData: () => ProposalFormPayload;
}

export const useProposalForm = ({
    isEditMode,
    fieldKeyFilter,
    displayKeyFilter
}: UseProposalFormOptions): UseProposalFormResult => {
    const [activeTab, setActiveTab] = useState<string>('general');
    const [proposalName, setProposalName] = useState<string>('');
    const [fieldConfiguration, setFieldConfiguration] = useState<FieldConfigurationState>({} as FieldConfigurationState);
    const [displayConfiguration, setDisplayConfiguration] = useState<DisplayConfigState>({} as DisplayConfigState);

    const isCreatingProposal = useSelector(proposalSelectors.createProposalLoading);
    const hasCreationError = useSelector(proposalSelectors.createProposalError);
    const creationErrorMessage = useSelector(proposalSelectors.createProposalErrorMessage);
    const isCreationSuccess = useSelector(proposalSelectors.createProposalSuccess);
    const isUpdatingProposal = useSelector(proposalSelectors.updateProposalLoading);
    const hasUpdateError = useSelector(proposalSelectors.updateProposalError);
    const updateErrorMessage = useSelector(proposalSelectors.updateProposalErrorMessage);
    const isUpdateSuccess = useSelector(proposalSelectors.updateProposalSuccess);

    const proposalToEdit = useSelector(proposalSelectors.getCurrentProposal);
    const isLoadingProposal = useSelector(proposalSelectors.getCurrentProposalLoading);
    const isProposalError = useSelector(proposalSelectors.getCurrentProposalError);

    // Populate form fields when proposal data is available (both create and edit modes)
    useEffect(() => {
        if (proposalToEdit) {
            const title = isEditMode ? proposalToEdit.title || '' : '';
            setProposalName(title);

            const filteredFieldConfig = Object.fromEntries(
                Object.entries(proposalToEdit.fieldConfiguration ?? {}).filter(([key]) => fieldKeyFilter(key))
            ) as FieldConfigurationState;
            setFieldConfiguration(filteredFieldConfig);

            const filteredDisplayConfig = Object.fromEntries(
                Object.entries(proposalToEdit.displayConfiguration ?? {}).filter(([key]) => displayKeyFilter(key))
            ) as DisplayConfigState;

            // Backfill missing display keys that pass the filter with { active: false }
            const backfilledDisplayConfig = Object.keys(DISPLAY_CONFIGURATION_LABELS).reduce((acc, key) => {
                if (displayKeyFilter(key) && !(key in filteredDisplayConfig)) {
                    acc[key] = { active: false };
                }
                return acc;
            }, {} as Record<string, { active: boolean }>);

            setDisplayConfiguration({ ...backfilledDisplayConfig, ...filteredDisplayConfig });
        }
    }, [proposalToEdit, fieldKeyFilter, displayKeyFilter, isEditMode]);

    const hasError = useMemo(() => {
        const proposalNameEmpty = !proposalName.trim();
        const proposalNameTooLong = proposalName.length > TEXT_INPUT_MAX_LENGTH;

        const fieldNameEmpty = Object.values(fieldConfiguration).some(
            (c) => typeof c.displayName === 'string' && !c.displayName.trim()
        );
        const fieldNameTooLong = Object.values(fieldConfiguration).some(
            (c) => typeof c.displayName === 'string' && c.displayName.length > TEXT_INPUT_MAX_LENGTH
        );

        const displayValues = Object.entries(displayConfiguration)
            .filter(([key]) => key !== 'dollarValueRounding' && key !== 'fontSizeAdjustment')
            .map(([, value]) => value) as Record<string, unknown>[];
        const displayNameEmpty = displayValues.some(
            (cfg) => 'displayName' in cfg && typeof cfg.displayName === 'string' && !cfg.displayName.trim()
        );
        const displayNameTooLong = displayValues.some(
            (cfg) => 'displayName' in cfg && typeof cfg.displayName === 'string' && cfg.displayName.length > TEXT_INPUT_MAX_LENGTH
        );

        const customDisclaimerTooLong = Boolean(
            displayConfiguration.customDisclaimer &&
                displayConfiguration.customDisclaimer.active &&
                typeof displayConfiguration.customDisclaimer.value === 'string' &&
                displayConfiguration.customDisclaimer.value.length > MAX_DISCLAIMER_LENGTH
        );

        const customDisclaimerBlank = Boolean(
            displayConfiguration.customDisclaimer &&
                displayConfiguration.customDisclaimer.active &&
                !displayConfiguration.customDisclaimer.value?.trim()
        );

        return (
            proposalNameEmpty ||
            proposalNameTooLong ||
            fieldNameEmpty ||
            fieldNameTooLong ||
            displayNameEmpty ||
            displayNameTooLong ||
            customDisclaimerTooLong ||
            customDisclaimerBlank
        );
    }, [proposalName, fieldConfiguration, displayConfiguration]);

    const handleDisplayNameChangeForFieldConfigItem = useCallback((fieldKey: FieldConfigurationKey, newDisplayName: string) => {
        setFieldConfiguration((prev) => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                displayName: newDisplayName
            }
        }));
    }, []);

    const handleBoldChangeForFieldConfigItem = useCallback((fieldKey: FieldConfigurationKey, checked: boolean) => {
        setFieldConfiguration((prev) => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                bold: checked
            }
        }));
    }, []);

    const handleActiveChangeForFieldConfigItem = useCallback(
        (fieldKey: FieldConfigurationKey | FourSquareFieldKey, checked: boolean) => {
            setFieldConfiguration((prev) => ({
                ...prev,
                [fieldKey]: {
                    ...prev[fieldKey as FieldConfigurationKey],
                    active: checked
                }
            }));
        },
        []
    );

    const handleOrderChangeForFieldConfig = useCallback((orderedKeys: FieldConfigurationKey[]) => {
        setFieldConfiguration((prev) => {
            return Object.fromEntries(
                orderedKeys.map((key, index) => [
                    key,
                    {
                        ...prev[key],
                        order: index + 1
                    }
                ])
            ) as FieldConfigurationState;
        });
    }, []);

    const handleDollarValueRoundingChange = useCallback((value: DollarValueRounding) => {
        setDisplayConfiguration(
            (prev) =>
                ({
                    ...prev,
                    dollarValueRounding: {
                        ...prev.dollarValueRounding,
                        value
                    }
                } as DisplayConfigState)
        );
    }, []);

    const handleFontSizeAdjustmentChange = useCallback((value: FontSizeAdjustment) => {
        setDisplayConfiguration(
            (prev) =>
                ({
                    ...prev,
                    fontSizeAdjustment: {
                        ...prev.fontSizeAdjustment,
                        active: true,
                        value
                    }
                } as DisplayConfigState)
        );
    }, []);

    const handleActiveChangeForDisplayConfigItem = useCallback(
        (displayKey: DisplayItemKey | FourSquareDisplayKey, checked: boolean) => {
            setDisplayConfiguration((prev) => ({
                ...prev,
                [displayKey]: {
                    ...prev[displayKey],
                    active: checked
                }
            }));
        },
        []
    );

    const handleValueChangeForDisplayConfigItem = useCallback(
        (displayKey: DisplayItemKey | FourSquareDisplayKey, value: string | undefined) => {
            setDisplayConfiguration((prev) => ({
                ...prev,
                [displayKey]: {
                    ...prev[displayKey],
                    value
                }
            }));
        },
        []
    );

    const handleDisplayNameChangeForDisplayConfigItem = useCallback((displayKey: DisplayItemKey, newDisplayName: string) => {
        setDisplayConfiguration(
            (prev) =>
                ({
                    ...prev,
                    [displayKey]: {
                        ...prev[displayKey],
                        displayName: newDisplayName
                    }
                } as DisplayConfigState)
        );
    }, []);

    const getFormData = useCallback(
        () => ({
            title: proposalName,
            fieldConfiguration,
            displayConfiguration
        }),
        [proposalName, fieldConfiguration, displayConfiguration]
    );

    return {
        activeTab,
        setActiveTab,
        proposalName,
        setProposalName,
        fieldConfiguration,
        displayConfiguration,

        isLoadingProposal,
        isProposalError,
        proposalToEdit,
        isCreatingProposal,
        hasCreationError,
        creationErrorMessage,
        isCreationSuccess,
        isUpdatingProposal,
        hasUpdateError,
        updateErrorMessage,
        isUpdateSuccess,

        hasError,

        handleDisplayNameChangeForFieldConfigItem,
        handleBoldChangeForFieldConfigItem,
        handleActiveChangeForFieldConfigItem,
        handleOrderChangeForFieldConfig,
        handleDollarValueRoundingChange,
        handleFontSizeAdjustmentChange,
        handleActiveChangeForDisplayConfigItem,
        handleValueChangeForDisplayConfigItem,
        handleDisplayNameChangeForDisplayConfigItem,

        getFormData
    };
};
